import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { CalendarDays, Utensils, BookOpen, ArrowRight, ChefHat, LineChart, FilePlus2, PenLine, FolderOpen, Clock, Users, Zap, Trash2 } from "lucide-react";
import Logo from "@/components/Logo";
import HeaderUtils from "@/components/HeaderUtils";
import AboutSPC from "@/components/AboutSPC";
import ProductTour from "@/components/ProductTour";
import BuildNextWeekNudge from "@/components/BuildNextWeekNudge";

import RecipeOfTheWeekButton from "@/components/RecipeOfTheWeekButton";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent } from "@/components/ui/dialog";

// Take a best-effort first name from the email — beats a bare "Welcome back".
// Handles firstname.lastname@… , first_last@… and plain first@… .
const firstNameFrom = (email) => {
  if (!email) return "";
  const local = email.split("@")[0] || "";
  const raw = local.split(/[._-]/)[0] || local;
  return raw ? raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase() : "";
};

export default function Home() {
  const navigate = useNavigate();
  const { email, planTier, hasAccess, isCoach } = useAuth();
  const isLite = planTier === "lite";
  const [planCount, setPlanCount] = useState(null);
  const [latestPlanName, setLatestPlanName] = useState("");
  const [savedPlans, setSavedPlans] = useState([]);
  const [profileName, setProfileName] = useState("");
  // Plan-intent picker — surfaces when the athlete already has ≥1 saved plan
  // and taps "Build / Edit". Beta feedback (Bethan, Aug 2026): the wizard
  // silently reopened her existing plan when she wanted a new week, and she
  // edited the wrong plan without realising. Explicit choice fixes that.
  const [planIntentOpen, setPlanIntentOpen] = useState(false);
  const [showPlanList, setShowPlanList] = useState(false);
  const firstName = (profileName.trim().split(/\s+/)[0] || firstNameFrom(email)).replace(/^./, (c) => c.toUpperCase());

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/plans");
        const plans = data || [];
        setPlanCount(plans.length);
        const sorted = [...plans].sort((a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0));
        setSavedPlans(sorted);
        // Prefer the most recently updated plan's profile.name — falls back to
        // the email prefix if the athlete never filled it in.
        const withName = sorted.find((p) => (p.profile?.name || "").trim());
        if (withName) setProfileName(withName.profile.name.trim());
        if (sorted[0]) setLatestPlanName(sorted[0].name || "");
      }
      catch (e) { console.error("Home: plan count failed", e); }
    })();
  }, []);

  const deletePlan = async (p) => {
    if (!window.confirm(`Delete "${p.name || "this plan"}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/plans/${p.id}`);
      const left = savedPlans.filter((x) => x.id !== p.id);
      setSavedPlans(left);
      setPlanCount(left.length);
      setLatestPlanName(left[0]?.name || "");
      if (!left.length) setShowPlanList(false);
      toast.success("Plan deleted");
    } catch {
      toast.error("Could not delete that plan");
    }
  };

  const onPlanCardClick = () => {
    // No plans yet → jump straight into a new one (nothing to edit).
    // No plans yet → straight into the quick build; it's the least daunting
    // first experience and the full wizard is one tap away from there.
    if (!planCount) { navigate("/app?intent=express"); return; }
    setShowPlanList(false);
    setPlanIntentOpen(true);
  };

  const cards = [
    ...(isCoach ? [{ key: "coach", title: "Athlete Race Plans", icon: Users, accent: "#E5E7EB", to: "/coach",
      desc: "Build race week & race day fuelling for the athletes you coach" }] : []),
    { key: "plan", title: "Build / Edit a Plan", icon: CalendarDays, accent: "#E2E30A", to: "/app",
      desc: planCount ? `Continue or start a week — ${planCount} saved` : "Create your personalised weekly fuelling plan" },
    { key: "tracker", title: "Open Tracker", icon: Utensils, accent: "#22D3EE", to: "/tracker",
      desc: "Log every meal, snack and drink against your plan" },
    ...(hasAccess && !isLite ? [{ key: "insights", title: "Insights", icon: LineChart, accent: "#FB7185", to: "/insights",
      desc: "See your 7-day calorie, macro and weight trends", paidOnly: true }] : []),
    { key: "guide", title: "Nutrition Guide", icon: BookOpen, accent: "#A3E635", to: "/learn",
      desc: "Plain-English answers on fuelling, macros & supplements" },
    ...(isLite ? [] : [{ key: "recipes", title: "Recipe Ideas", icon: ChefHat, accent: "#FB923C", to: "/recipes",
      desc: "Meals & snacks for everyone — plus recipes shared by the community" }]),
  ];
  // Tracker leads once there's a plan to track against — before that the
  // only sensible first step is building the plan.
  const hasPlan = (planCount || 0) > 0;
  const primaryKey = hasPlan ? "tracker" : "plan";
  const primary = planCount === null ? null : cards.find((c) => c.key === primaryKey);
  const secondary = cards.filter((c) => c.key !== (primary ? primary.key : null));

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <header className="sticky top-0 z-40 bg-black/90 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 min-h-[60px] py-2.5 flex items-center gap-3">
          <Logo className="h-9 w-9 sm:h-11 sm:w-11 shrink-0" />
          <div className="leading-tight min-w-0">
            <p className="font-display text-sm sm:text-xl uppercase tracking-tight">
              <span className="text-[#E2E30A]">Performance</span> Nutrition
            </p>
            <p className="hidden sm:block text-[10px] uppercase tracking-[0.28em] text-zinc-200 mt-1">Powered by SPC</p>
          </div>
          <div className="ml-auto" data-tour-step="profile-menu"><HeaderUtils /></div>
        </div>
      </header>

      <main data-testid="home-page" className="max-w-4xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#E2E30A] font-bold">Welcome back{firstName ? `, ${firstName}` : ""}</p>
          <h1 className="font-display text-4xl sm:text-5xl uppercase tracking-tighter font-black mt-2 leading-[0.92]">
            What are we<br />doing today?
          </h1>
        </motion.div>

        <BuildNextWeekNudge />

        {/* One primary action gets real weight — everything else is a compact
            secondary grid, so the daily tap target is unmistakable. */}
        <div className="mt-8 grid gap-3">
          {primary && (
            <motion.button data-testid={`home-card-${primary.key}`}
              onClick={primary.key === "plan" ? onPlanCardClick : () => navigate(primary.to)}
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.08 }}
              data-tour-step={primary.key === "tracker" ? "home-open-tracker" : "home-build-plan"}
              className="group relative text-left overflow-hidden border border-[#E2E30A]/60 bg-[#E2E30A]/[0.07] hover:bg-[#E2E30A]/[0.12] transition-colors px-5 py-6 sm:px-7 sm:py-8 flex items-center gap-5">
              <span className="absolute inset-y-0 left-0 w-[3px] bg-[#E2E30A]" />
              <span className="grid place-items-center h-14 w-14 sm:h-16 sm:w-16 shrink-0 bg-[#E2E30A]"
                style={{ boxShadow: "0 10px 30px -16px #E2E30A" }}>
                <primary.icon className="h-7 w-7 sm:h-8 sm:w-8 text-black" strokeWidth={2.2} />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-[10px] uppercase tracking-[0.28em] text-[#E2E30A] font-bold">{hasPlan ? "Start here" : "First step"}</span>
                <span className="block font-display text-2xl sm:text-3xl uppercase tracking-tighter font-black text-white mt-1 leading-none">{hasPlan ? primary.title : "Build your plan"}</span>
                <span className="block text-xs sm:text-sm text-zinc-300 mt-2 leading-snug">{primary.desc}</span>
              </span>
              <ArrowRight className="h-5 w-5 shrink-0 text-[#E2E30A] group-hover:translate-x-1 transition-transform" />
            </motion.button>
          )}

          <div className="grid grid-cols-2 gap-3">
            {secondary.map((c, i) => {
              const Icon = c.icon;
              const handler = c.key === "plan" ? onPlanCardClick : () => navigate(c.to);
              return (
                <motion.button key={c.key} data-testid={`home-card-${c.key}`} onClick={handler}
                  initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.16 + i * 0.07 }}
                  className="group relative text-left border border-white/10 bg-[#141414] hover:border-white/30 hover:bg-white/[0.04] transition-colors p-4 sm:p-5 flex flex-col min-h-[132px]"
                  data-tour-step={c.key === "plan" ? "home-build-plan" : c.key === "tracker" ? "home-open-tracker" : c.key === "recipes" ? "home-recipe" : undefined}>
                  <span className="absolute inset-y-0 left-0 w-[2px] opacity-0 group-hover:opacity-100 transition-opacity" style={{ backgroundColor: c.accent }} />
                  <span className="grid place-items-center h-10 w-10 shrink-0 border border-white/15 bg-white/[0.04] group-hover:border-white/30 transition-colors">
                    <Icon className="h-[18px] w-[18px]" strokeWidth={2.1} style={{ color: c.accent }} />
                  </span>
                  <span className="block text-[11px] sm:text-xs font-bold uppercase tracking-[0.16em] text-white mt-3 leading-tight break-words">{c.title}</span>
                  <span className="block text-[11px] text-zinc-400 mt-1.5 leading-snug break-words flex-1">{c.desc}</span>
                  <ArrowRight className="h-3.5 w-3.5 mt-2.5 text-white/35 group-hover:text-white group-hover:translate-x-0.5 transition-all" />
                </motion.button>
              );
            })}
          </div>
        </div>

        {/* Recipe-of-the-week sits under the actions — inspiration after intent. */}
        <RecipeOfTheWeekButton />

        <Dialog open={planIntentOpen} onOpenChange={setPlanIntentOpen}>
          <DialogContent data-testid="plan-intent-dialog"
            className="bg-[#141414] border border-[#E2E30A]/40 rounded-none text-white max-w-md p-0 gap-0"
            style={{ boxShadow: "0 0 0 1px rgba(226,227,10,0.15), 0 20px 60px rgba(0,0,0,0.6)" }}>
            <div className="px-6 pt-6 pb-2">
              <p className="text-[10px] uppercase tracking-[0.28em] text-[#E2E30A] font-bold">Weekly Plan</p>
              <h2 className="font-display text-2xl sm:text-3xl uppercase tracking-tight mt-2 leading-tight">Are we building a new week — or editing an existing one?</h2>
              <p className="text-xs text-zinc-300 mt-3 leading-relaxed">
                Pick <b className="text-white">New week</b> to start a fresh plan (your profile, dietary and macro
                prefs are pre-filled). Pick <b className="text-white">Continue editing</b> to open your most recent
                plan{latestPlanName ? <> — <span className="text-[#E2E30A]">{latestPlanName}</span></> : ""}.
              </p>
            </div>
            <div className="grid gap-2.5 px-6 py-5">
              <button type="button" data-testid="plan-intent-express"
                onClick={() => { setPlanIntentOpen(false); navigate("/app?intent=express"); }}
                className="group text-left border border-[#E2E30A] bg-[#E2E30A]/[0.14] hover:bg-[#E2E30A]/[0.2] px-4 py-3.5 flex items-center gap-3 transition-colors">
                <span className="grid place-items-center h-10 w-10 bg-[#E2E30A] text-black shrink-0">
                  <Zap className="h-5 w-5" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-bold uppercase tracking-wider text-white">Quick build · 1 minute</span>
                  <span className="block text-[11px] text-zinc-300 mt-0.5">Six answers, we do the rest. Tweak it after.</span>
                </span>
                <ArrowRight className="h-4 w-4 text-[#E2E30A] group-hover:translate-x-1 transition-transform" />
              </button>
              <button type="button" data-testid="plan-intent-new"
                onClick={() => { setPlanIntentOpen(false); navigate("/app?intent=new"); }}
                className="group text-left border border-[#E2E30A] bg-[#E2E30A]/[0.08] hover:bg-[#E2E30A]/[0.14] px-4 py-3.5 flex items-center gap-3 transition-colors">
                <span className="grid place-items-center h-10 w-10 bg-[#E2E30A] text-black shrink-0">
                  <FilePlus2 className="h-5 w-5" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-bold uppercase tracking-wider text-white">Start a new week</span>
                  <span className="block text-[11px] text-zinc-300 mt-0.5">The full nine steps — every detail yours to set.</span>
                </span>
                <ArrowRight className="h-4 w-4 text-[#E2E30A] group-hover:translate-x-1 transition-transform" />
              </button>
              <button type="button" data-testid="plan-intent-continue"
                onClick={() => { setPlanIntentOpen(false); navigate("/app"); }}
                className="group text-left border border-white/15 bg-[#0F0F0F] hover:border-white/40 hover:bg-white/[0.03] px-4 py-3.5 flex items-center gap-3 transition-colors">
                <span className="grid place-items-center h-10 w-10 border border-white/20 text-[#4C9AFF] shrink-0">
                  <PenLine className="h-5 w-5" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-bold uppercase tracking-wider text-white">Continue editing</span>
                  <span className="block text-[11px] text-zinc-300 mt-0.5 truncate">
                    Reopen {latestPlanName ? <span className="text-[#4C9AFF]">{latestPlanName}</span> : "your most recent plan"}
                  </span>
                </span>
                <ArrowRight className="h-4 w-4 text-white/60 group-hover:translate-x-1 transition-transform" />
              </button>
              {savedPlans.length >= 1 && (
                <button type="button" data-testid="plan-intent-pick"
                  onClick={() => setShowPlanList((s) => !s)}
                  aria-expanded={showPlanList}
                  className="group text-left border border-white/15 bg-[#0F0F0F] hover:border-white/40 hover:bg-white/[0.03] px-4 py-3.5 flex items-center gap-3 transition-colors">
                  <span className="grid place-items-center h-10 w-10 border border-white/20 text-[#FFB454] shrink-0">
                    <FolderOpen className="h-5 w-5" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-bold uppercase tracking-wider text-white">Pick a saved week</span>
                    <span className="block text-[11px] text-zinc-300 mt-0.5">
                      Choose any of your {savedPlans.length} saved plan{savedPlans.length === 1 ? "" : "s"} by name
                    </span>
                  </span>
                  <ArrowRight className={`h-4 w-4 text-white/60 transition-transform ${showPlanList ? "rotate-90" : ""}`} />
                </button>
              )}
              {showPlanList && savedPlans.length >= 1 && (
                <div data-testid="plan-intent-pick-list"
                  className="border border-white/10 bg-[#0A0A0A] max-h-64 overflow-y-auto">
                  {savedPlans.map((p) => (
                    <div key={p.id} className="flex items-center gap-1 border-b border-white/5 last:border-b-0 hover:bg-white/[0.04] transition-colors">
                      <button type="button" data-testid={`plan-intent-pick-${p.id}`}
                        onClick={() => { setPlanIntentOpen(false); navigate(`/app?load=${p.id}`); }}
                        className="flex-1 min-w-0 text-left px-4 py-3 flex items-center gap-3">
                        <Clock className="h-3.5 w-3.5 text-zinc-500 shrink-0" />
                        <span className="min-w-0 flex-1">
                          <span className="block text-sm text-white truncate">{p.name || "Untitled plan"}</span>
                          <span className="block text-[10px] text-zinc-400 font-mono mt-0.5">
                            Step {p.currentStep || 1}/8 · {p.updatedAt ? new Date(p.updatedAt).toLocaleDateString() : "—"}
                          </span>
                        </span>
                        <ArrowRight className="h-3.5 w-3.5 text-white/50 shrink-0" />
                      </button>
                      <button type="button" data-testid={`plan-intent-delete-${p.id}`}
                        onClick={() => deletePlan(p)} aria-label={`Delete ${p.name || "plan"}`}
                        className="shrink-0 grid place-items-center h-9 w-9 mr-2 text-zinc-500 hover:text-[#FF3B30] hover:bg-[#FF3B30]/10 transition-colors">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        <AboutSPC />
      </main>
      <ProductTour />
    </div>
  );
}
